/**
 * A stretch of time with a reason attached, and a clock you can walk away from.
 *
 * Its own module rather than more of screens.js, which is already long enough
 * that finding anything in it is a scroll. Everything here is about one
 * screen and one pill.
 *
 * **The clock is never accumulated.** Every repaint asks `elapsedSeconds` what
 * the session's own record and the wall clock say, so a phone that slept, a
 * tab that was closed and a browser that skipped a hundred frames all come
 * back to the right number instead of to a drifted one. This is the rule the
 * desktop timer arrived at in `44e950c`, for the same reason.
 *
 * **The timer lives on the server.** That is what lets the laptop see it, and
 * what makes closing the tab cost nothing.
 */
import * as ui from './ui.js';
import * as api from './sessions.js';
import {
  BREAK, PAUSE, elapsedSeconds, formatClock, notCountedSeconds, openHoldKind,
  timerScreenState,
} from './timer.js';
import { shouldPoll } from './heartbeat.js';
import { onSessionEnded } from './agreements.js';

let source = null;          // { config, session } once signed in
let live = null;            // the running or paused session, as the API sees it
let heart = null;           // the one interval, for the whole signed-in app
let ticks = 0;              // seconds since the heartbeat started
let confirmingFinish = false;
let confirmingCancel = false;

/* Under this and Done asks a different question. Allen, 2026-09-01: "ask for
 * a confirmation if you really want to [click done] on a study session if its
 * been less than 5 mins."
 *
 * Done has always taken two taps -- it is not a new confirmation, it is the
 * existing one saying why it is asking. A minute into a block, "Done" is far
 * more likely to be a misfire or a Cancel that grabbed the wrong button than
 * a sitting that is genuinely finished, and the prompt should say so. Over
 * five minutes it reads exactly as it always has. */
export const SHORT_SESSION_SECONDS = 5 * 60;
// A block tapped on the calendar: known, but not started. Cleared the moment
// a session begins, so the screen can never offer to start something twice.
let chosen = null;
// Called after a session ends, so today's list can redraw with the item now
// ticked. A hook rather than an import: screens.js reaches into this module,
// and importing it back would be a cycle.
let afterEnded = null;

const nowSeconds = () => Date.now() / 1000;

/** What is running right now, as the API last described it. */
export function liveSession() { return live; }

export function setSessionEndedHook(fn) { afterEnded = fn; }

export function setSessionSource(config, session) {
  source = config && session ? { config, session } : null;
  live = null;
  confirmingFinish = false;
  confirmingCancel = false;
  chosen = null;
  stopHeartbeat();
  paintHomeTimer();
  if (source) startHeartbeat();
}

/**
 * One interval for the whole signed-in app, not one per screen.
 *
 * It used to be started by `openTimer` and cleared on leaving, which is why
 * the pill's clock never moved: nothing repainted it once you were anywhere
 * else. And nothing re-read the session at all, so a timer started on the
 * phone never appeared on a laptop tab that was already open. Both were the
 * same missing thing.
 *
 * Repainting is free and happens every second. Asking the server is not, and
 * happens on the schedule in heartbeat.js -- never while the tab is hidden,
 * and immediately when it stops being.
 */
function startHeartbeat() {
  ticks = 0;
  heart = setInterval(async () => {
    ticks += 1;
    paintHomeTimer();
    if (!ui.el('timer-view').hidden) paintTimer();

    if (shouldPoll({ tick: ticks, live: Boolean(live) && live.state !== 'done', visible: visible() })) {
      await refreshLiveSession();
    }
  }, 1000);

  document.addEventListener('visibilitychange', onVisible);
}

const visible = () => document.visibilityState !== 'hidden';

/** Coming back to the app is the moment its screen is most likely stale. */
function onVisible() {
  if (visible() && source) refreshLiveSession();
}

function stopHeartbeat() {
  if (heart !== null) {
    clearInterval(heart);
    heart = null;
  }
  document.removeEventListener('visibilitychange', onVisible);
}

/** What is running, if anything. Never throws at the caller. */
export async function refreshLiveSession() {
  if (!source) return null;
  try {
    live = await api.currentSession(source.config, source.session);
  } catch {
    // A timer that cannot be read is not worth a red line on the home screen.
    // The pill simply does not appear, and the next open asks again.
    live = null;
  }
  paintHomeTimer();
  return live;
}

/** The pill on the home screen: the clock, from wherever you are. */
export function paintHomeTimer() {
  const pill = ui.el('home-timer');
  const running = Boolean(live) && live.state !== 'done';
  pill.hidden = !running;
  if (!running) return;
  ui.el('home-timer-reason').textContent = live.reason;
  ui.el('home-timer-clock').textContent = formatClock(elapsedSeconds(live, nowSeconds()));
  ui.el('home-timer-state').textContent = live.state === 'paused' ? 'Paused' : 'Running';
}

/* ── the unscheduled session: a name before a clock ─────────── */

export function openUnscheduled() {
  ui.el('unscheduled-form').hidden = false;
  ui.el('unscheduled-error').hidden = true;
  const input = ui.el('unscheduled-input');
  input.value = '';
  // Focused on opening, because the next thing to happen is typing. A field
  // that appears and then waits to be tapped asks for two gestures where one
  // would do.
  input.focus();
}

export function cancelUnscheduled() {
  ui.el('unscheduled-form').hidden = true;
  ui.el('unscheduled-input').value = '';
}

export async function submitUnscheduled(event) {
  event?.preventDefault();
  if (!source) return;

  const input = ui.el('unscheduled-input');
  const reason = input.value.trim();
  if (!reason) {
    // An unnamed stretch of time is a number nobody can act on later, so it
    // is refused rather than saved as "Untitled".
    input.focus();
    return;
  }

  const button = ui.el('unscheduled-start');
  button.disabled = true;
  try {
    live = await api.startSession(source.config, source.session, reason);
    cancelUnscheduled();
    openTimer();
  } catch (error) {
    if (error?.alreadyRunning) {
      // The other device got there first. Showing the session that exists is
      // a better answer than an error about the one that did not start.
      cancelUnscheduled();
      await refreshLiveSession();
      openTimer();
      return;
    }
    const note = ui.el('unscheduled-error');
    note.textContent = error?.message || 'Could not start the timer.';
    note.hidden = false;
  } finally {
    button.disabled = false;
  }
}

/**
 * Start a timer against an assignment, from the agenda.
 *
 * Delegated, because the agenda rows are rebuilt on every Canvas refresh and
 * a listener bound to a row would go with it. The assignment's id and title
 * ride on the element for the same reason.
 *
 * The reason is the assignment's own title rather than something typed: the
 * student already said what they are working on by tapping it.
 */
export async function startForAssignment(event) {
  const button = event.target.closest('.agenda-start');
  if (!button || !source) return;

  const reason = button.dataset.reason || 'Assignment';
  const workItemId = button.dataset.workItem || null;

  button.disabled = true;
  try {
    live = await api.startSession(source.config, source.session, reason, workItemId);
    openTimer();
  } catch (error) {
    if (error?.alreadyRunning) {
      // Something is already running -- possibly on the laptop. Show it
      // rather than refusing, which is the answer that helps.
      await refreshLiveSession();
      openTimer();
      return;
    }
    // The agenda has no error line of its own, and inventing one for this
    // would put a red row in a list of assignments. The pill stays as it was.
  } finally {
    button.disabled = false;
  }
}

/* ── the timer screen ───────────────────────────────────────── */

export function openTimer() {
  ui.showView('timer');
  confirmingFinish = false;
  confirmingCancel = false;
  paintTimer();
}

/**
 * Open the timer for a study block tapped on the calendar.
 *
 * Ready, not running. Allen's decision, and the reason is that only one
 * session may run at a time: a mis-tap that started the clock would record
 * time against the wrong assignment and have to be ended to undo, which then
 * feeds the estimates that time is supposed to make better.
 */
export function openTimerFor(block) {
  chosen = block && block.reason ? block : null;
  openTimer();
}

/** Start the block that is waiting. */
export async function startChosen() {
  if (!source || !chosen) return;
  const button = ui.el('timer-start');
  button.disabled = true;
  try {
    live = await api.startSession(
      source.config, source.session, chosen.reason, chosen.workItemId ?? null);
    chosen = null;
  } catch (error) {
    // Something is already running, possibly on the laptop. Show it rather
    // than refusing -- the same answer `startForAssignment` gives.
    if (error?.alreadyRunning) await refreshLiveSession();
  } finally {
    button.disabled = false;
  }
  paintTimer();
  paintHomeTimer();
}

export function leaveTimer() {
  confirmingFinish = false;
  confirmingCancel = false;
  chosen = null;
  paintHomeTimer();
  ui.showHome();
}

export function paintTimer() {
  const face = timerScreenState(live, chosen);
  const start = ui.el('timer-start');

  if (face !== 'running') {
    // Nothing is running. Either a block is waiting to be started, or there
    // is nothing to draw at all and the screen is left as it was.
    start.hidden = face !== 'ready';
    if (face !== 'ready') return;
    ui.el('timer-clock').textContent = formatClock(0);
    ui.el('timer-idle').textContent = formatClock(0);
    ui.el('timer-clock').classList.remove('is-paused');
    ui.el('timer-reason').textContent = chosen.reason;
    ui.el('timer-state').textContent = chosen.courseCode
      ? `${chosen.courseCode} · not started`
      : 'Not started';
    ui.el('timer-break').hidden = true;
    ui.el('timer-pause').hidden = true;
    ui.el('timer-finish').hidden = true;
    return;
  }

  const now = nowSeconds();
  start.hidden = true;
  ui.el('timer-clock').textContent = formatClock(elapsedSeconds(live, now));
  ui.el('timer-idle').textContent = formatClock(notCountedSeconds(live, now));
  ui.el('timer-reason').textContent = live.reason;

  const paused = live.state === 'paused';
  const done = live.state === 'done';
  const hold = paused ? (openHoldKind(live) || BREAK) : '';
  // "Alter started this" is a different thing to read than "you started
  // this", and the student should not have to work out which.
  const whose = live.origin === 'desktop' ? 'Started by Alter · ' : '';
  ui.el('timer-state').textContent = whose + (
    done ? 'Finished'
      : hold === BREAK ? 'On a break'
        : paused ? 'Paused'
          : 'Running');
  ui.el('timer-clock').classList.toggle('is-paused', paused);

  /* One button while a hold is open, and it is Resume. Two of the three
   * would already be true, which is a row nobody can read. */
  const breakButton = ui.el('timer-break');
  const pause = ui.el('timer-pause');
  breakButton.hidden = done || paused;
  pause.hidden = done;
  pause.textContent = paused ? 'Resume' : 'Pause';
  pause.classList.toggle('btn-wide', paused);

  const finish = ui.el('timer-finish');
  finish.hidden = done;
  // Pause, then tap again to confirm you are done. A single tap that ends a
  // session is a session ended by a pocket.
  //
  // Under five minutes the second tap says how long it has actually been.
  // The confirmation was always here; what was missing was the reason for it.
  finish.textContent = confirmingFinish
    ? finishPrompt(elapsedSeconds(live, now))
    : 'Done';
  finish.classList.toggle('is-confirming', confirmingFinish);

  const cancel = ui.el('timer-cancel');
  // Never on a finished session. Cancel is for a clock in front of you, and
  // the API refuses one that is already `done` -- offering a button that
  // cannot work is how a dead button gets built.
  cancel.hidden = done;
  cancel.textContent = confirmingCancel
    ? 'Tap again to discard — no time will be recorded'
    : 'Cancel this timer';
  cancel.classList.toggle('is-confirming', confirmingCancel);
}

/** What the second tap on Done should say, given how long it has run. */
export function finishPrompt(seconds) {
  if (!(seconds < SHORT_SESSION_SECONDS)) return 'Tap again to finish';
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  const howLong = minutes < 1 ? 'under a minute' : `only ${minutes} min`;
  return `${howLong} — tap again to finish`;
}

/**
 * Pause, or resume whichever hold is open.
 *
 * Still one handler for the one button, because while a hold is open the
 * button says Resume -- and a resume ends whatever kind was running, so it
 * does not have to know which.
 */
export async function toggleTimerPause() {
  if (live && live.state === 'paused') return holdTimer(null);
  return holdTimer(PAUSE);
}

/** Step away without ending the sitting. The not-counted clock keeps going. */
export async function takeABreak() {
  return holdTimer(BREAK);
}

async function holdTimer(kind) {
  if (!source || !live) return;
  confirmingFinish = false;
  confirmingCancel = false;
  try {
    live = kind === null
      ? await api.resumeSession(source.config, source.session, live.sessionId)
      : await api.pauseSession(source.config, source.session, live.sessionId, kind);
  } catch {
    // Leave the screen showing what the server thinks. A pause that did not
    // arrive has not happened, and pretending otherwise loses the minutes.
    await refreshLiveSession();
  }
  paintTimer();
  // The pill on the home screen is the same fact seen from elsewhere, and it
  // says Running or Paused. Left unpainted it went on saying Running until the
  // next heartbeat, which is the app disagreeing with itself on the screen you
  // just pressed.
  paintHomeTimer();
}

/**
 * Throw the sitting away. "It was accidentally started."
 *
 * Two taps, like Done, so the one destructive control on this screen is not
 * easier to trigger than the safe one. Arming it disarms Done: two buttons
 * both reading "tap again" is a row where the second tap lands on whichever
 * one your thumb is nearer, and one of them erases your afternoon.
 */
export async function cancelTimer() {
  if (!source || !live) return;
  if (!confirmingCancel) {
    confirmingCancel = true;
    confirmingFinish = false;
    confirmingCancel = false;
    paintTimer();
    return;
  }
  confirmingCancel = false;
  try {
    await api.cancelSession(source.config, source.session, live.sessionId);
  } catch {
    // A cancel that did not reach the server has not happened. Show what the
    // server actually thinks rather than a screen that has moved on without
    // it -- the same rule `holdTimer` keeps.
    await refreshLiveSession();
    paintTimer();
    paintHomeTimer();
    return;
  }
  live = null;
  paintTimer();
  paintHomeTimer();
  /* No `onSessionEnded`. That is the agreement that a timer ending ticks the
   * item it ran against, and a cancelled timer ran against nothing as far as
   * anything downstream is concerned. Ticking an assignment off because a
   * misfired timer was discarded is the worst outcome this button has. */
  if (afterEnded) afterEnded();
  leaveTimer();
}

export async function finishTimer() {
  if (!source || !live) return;
  if (!confirmingFinish) {
    confirmingFinish = true;
    // Same reason as above, the other way round.
    confirmingCancel = false;
    paintTimer();
    return;
  }
  const ending = live;
  let ended = false;
  try {
    live = await api.endSession(source.config, source.session, live.sessionId, null);
    ended = true;
  } catch {
    await refreshLiveSession();
  }
  confirmingFinish = false;
  confirmingCancel = false;
  paintTimer();
  paintHomeTimer();

  if (ended) {
    // The second of the three agreements: a timer that ends ticks the item it
    // was running against. An unscheduled break was running against nothing,
    // so it ticks nothing -- that is the difference between the two.
    await onSessionEnded(
      { markDone: api.markDone }, source.config, source.session, ending,
    );
    if (afterEnded) afterEnded();
    /* And then leave. Allen, 2026-08-19: "if you click tap again to finish it
     * just keeps you on the timer page it should make you leave."
     *
     * Not a cosmetic complaint. The screen it left you on says "Finished"
     * over a stopped clock with no controls, which is indistinguishable from
     * a timer that failed to end -- and the only way out was a back arrow, on
     * the one screen where the thing you just did was final.
     *
     * Only on success. A finish that did not reach the server has to leave
     * you looking at the session that is still running.
     */
    leaveTimer();
  }
}

/* ── "it was still running yesterday -- when did it end?" ───── */

/**
 * The second half of the forgotten-timer rule.
 *
 * The cap stops the number being absurd; this stops it being silently wrong.
 * Asked once, on the first open that sees it, and dismissing keeps the capped
 * value -- still marked a guess in the record, it just stops asking.
 */
export async function askAboutForgotten() {
  const session = await refreshLiveSession();
  const guessed = Boolean(session) && session.state === 'done' && session.endedAtInferred;
  ui.el('forgotten').hidden = !guessed;
  if (!guessed) return;

  const started = new Date(session.startedAt * 1000);
  ui.el('forgotten-note').textContent =
    `"${session.reason}" was still running from `
    + `${started.toLocaleString([], { weekday: 'long', hour: 'numeric', minute: '2-digit' })}. `
    + 'It stopped counting after four hours. When did you actually finish?';
}

export async function answerForgotten() {
  if (!source || !live) return;
  const value = ui.el('forgotten-time').value;
  if (!value) return;
  // A datetime-local value is read in the browser's own zone, which is the
  // zone the student answered in. Epoch seconds from there, never a string.
  const endedAt = new Date(value).getTime() / 1000;
  if (!Number.isFinite(endedAt)) return;
  try {
    await api.endSession(source.config, source.session, live.sessionId, endedAt);
  } catch {
    // The guess stays and it will ask again, which is the honest outcome.
  }
  dismissForgotten();
}

export function dismissForgotten() {
  ui.el('forgotten').hidden = true;
  live = null;
  paintHomeTimer();
}
